import { createBrowserRouter, Navigate } from "react-router-dom";
import LandingPage from "@/pages/landing";
import LoginPage from "@/pages/login";
import RegisterPage from "@/pages/register";
import ForgotPasswordPage from "@/pages/forgot-password";
import ChangePasswordPage from "@/pages/change-password";
import SuperadminLoginPage from "@/pages/superadmin-login";
import InvitePage from "@/pages/invite";
import WatchPage from "@/pages/watch";
import ComparePage from "@/pages/compare";
import VersionsPage from "@/pages/versions";
import ShareEntryPage from "@/pages/share-entry";
import { ShareFolderPage } from "@/pages/share-folder";
import { ShareWatchPage } from "@/pages/share-watch";
import NotFoundPage from "@/pages/not-found";
import RouteErrorBoundary from "@/pages/route-error";
import { PlaceholderPage } from "@/pages/placeholder";
import { ProjectsPage } from "@/pages/projects";
import { ProjectDetailPage } from "@/pages/project-detail";
import { FolderDetailPage } from "@/pages/folder-detail";
import { AllSharesPage } from "@/pages/all-shares";
import { EditSharePage } from "@/pages/edit-share";
import { UploadPage } from "@/pages/upload";
import { TrashPage } from "@/pages/trash";
import { NotificationsPage } from "@/pages/notifications";
import AppLayout from "@/layouts/app-layout";
import AdminLayout from "@/layouts/admin-layout";
import SuperadminLayout from "@/layouts/superadmin-layout";
import { RequireAuth } from "@/components/auth/require-auth";
import { RequireRole } from "@/components/auth/require-role";
import { RequireSuperadmin } from "@/components/auth/require-superadmin";
import { SettingsShell } from "@/pages/settings/settings-shell";
import { ProfileSettingsPage } from "@/pages/settings/profile";
import { NotificationsSettingsPage } from "@/pages/settings/notifications";
import { UsageSettingsPage } from "@/pages/settings/usage";
import { StorageSettingsPage } from "@/pages/settings/storage";
import { PlanSettingsPage } from "@/pages/settings/plan";
import { BillingSettingsPage } from "@/pages/settings/billing";
import { BrandingSettingsPage } from "@/pages/settings/branding";
import { LabsSettingsPage } from "@/pages/settings/labs";
import { UsersSettingsPage } from "@/pages/settings/users";
import { ProjectsSettingsPage } from "@/pages/settings/projects";
import { FieldsSettingsPage } from "@/pages/settings/fields";
import { ContentSecuritySettingsPage } from "@/pages/settings/content-security";
import { ActionsSettingsPage } from "@/pages/settings/actions";
import { WebhooksSettingsPage } from "@/pages/settings/webhooks";
import { LegalNoticesSettingsPage } from "@/pages/settings/legal-notices";
import SearchPage from "@/pages/search";
import SuperadminDashboard from "@/pages/superadmin/dashboard";

export const router = createBrowserRouter([
  // ── Public ────────────────────────────────────────────────────────────────
  { path: "/", element: <LandingPage />, errorElement: <RouteErrorBoundary /> },
  { path: "/login", element: <LoginPage /> },
  { path: "/register", element: <RegisterPage /> },
  { path: "/forgot-password", element: <ForgotPasswordPage /> },
  { path: "/superadmin/login", element: <SuperadminLoginPage /> },
  { path: "/invite/:token", element: <InvitePage /> },
  { path: "/watch/:token", element: <WatchPage /> },

  // ── First-class shares (migration 0013). /s/:token is the entry — resolves
  //    to a new-model share, falls back to legacy /watch/:token on 404. ─────
  { path: "/s/:token", element: <ShareEntryPage /> },
  { path: "/s/:token/f/:folderId", element: <ShareFolderPage /> },
  { path: "/s/:token/v/:videoId", element: <ShareWatchPage /> },

  // ── Post-login forced flow ────────────────────────────────────────────────
  {
    element: <RequireAuth />,
    children: [
      { path: "/change-password", element: <ChangePasswordPage /> },
    ],
  },

  // ── Editor / workspace-member area ────────────────────────────────────────
  {
    path: "/app",
    element: <RequireAuth />,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        element: <AppLayout />,
        children: [
          { index: true, element: <Navigate to="/app/projects" replace /> },
          {
            path: "projects",
            element: <ProjectsPage basePath="/app/projects" settingsPath="/app/settings" />,
          },
          {
            path: "projects/:projectId",
            element: (
              <ProjectDetailPage
                projectsBasePath="/app/projects"
                uploadPath="/app/upload"
                watchPath="/app/watch"
              />
            ),
          },
          {
            path: "projects/:projectId/shares",
            element: <AllSharesPage projectsBasePath="/app/projects" />,
          },
          {
            path: "projects/:projectId/shares/:shareId",
            element: <EditSharePage projectsBasePath="/app/projects" />,
          },
          {
            path: "projects/:projectId/folders/:folderId",
            element: (
              <FolderDetailPage
                projectsBasePath="/app/projects"
                uploadPath="/app/upload"
                watchPath="/app/watch"
              />
            ),
          },
          {
            path: "upload",
            element: <UploadPage projectsBasePath="/app/projects" />,
          },
          {
            path: "versions/:videoId",
            element: <VersionsPage />,
          },
          {
            path: "trash",
            element: <TrashPage />,
          },
          {
            path: "notifications",
            element: <NotificationsPage watchPath="/app/watch" basePrefix="/app" />,
          },
          {
            path: "search",
            element: <SearchPage />,
          },
          {
            path: "settings",
            element: <SettingsShell basePath="/app/settings" />,
            children: [
              { index: true, element: <Navigate to="/app/settings/profile" replace /> },
              { path: "profile", element: <ProfileSettingsPage /> },
              { path: "notifications", element: <NotificationsSettingsPage /> },
              { path: "usage", element: <UsageSettingsPage /> },
              { path: "storage", element: <StorageSettingsPage /> },
              { path: "plan", element: <PlanSettingsPage /> },
              { path: "billing", element: <BillingSettingsPage /> },
              { path: "branding", element: <BrandingSettingsPage /> },
              { path: "labs", element: <LabsSettingsPage /> },
              { path: "actions", element: <ActionsSettingsPage /> },
              { path: "webhooks", element: <WebhooksSettingsPage /> },
              { path: "legal", element: <LegalNoticesSettingsPage /> },
            ],
          },
        ],
      },
    ],
  },

  // ── Workspace-admin area ──────────────────────────────────────────────────
  {
    path: "/admin",
    element: <RequireRole roles={["ADMIN"]} />,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        element: <AdminLayout />,
        children: [
          { index: true, element: <Navigate to="/admin/projects" replace /> },
          {
            path: "projects",
            element: (
              <ProjectsPage
                basePath="/admin/projects"
                editorsPath="/admin/editors"
                settingsPath="/admin/settings"
              />
            ),
          },
          {
            path: "projects/:projectId",
            element: (
              <ProjectDetailPage
                projectsBasePath="/admin/projects"
                uploadPath="/admin/upload"
                watchPath="/admin/watch"
              />
            ),
          },
          {
            path: "projects/:projectId/shares",
            element: <AllSharesPage projectsBasePath="/admin/projects" />,
          },
          {
            path: "projects/:projectId/shares/:shareId",
            element: <EditSharePage projectsBasePath="/admin/projects" />,
          },
          {
            path: "projects/:projectId/folders/:folderId",
            element: (
              <FolderDetailPage
                projectsBasePath="/admin/projects"
                uploadPath="/admin/upload"
                watchPath="/admin/watch"
              />
            ),
          },
          {
            path: "upload",
            element: <UploadPage projectsBasePath="/admin/projects" />,
          },
          {
            path: "versions/:videoId",
            element: <VersionsPage />,
          },
          {
            path: "trash",
            element: <TrashPage />,
          },
          {
            path: "editors",
            element: (
              <PlaceholderPage
                title="Admin — Editors"
                description="Invite, remove, and manage editor permissions."
              />
            ),
          },
          {
            path: "notifications",
            element: <NotificationsPage watchPath="/admin/watch" basePrefix="/admin" />,
          },
          {
            path: "search",
            element: <SearchPage />,
          },
          {
            path: "settings",
            element: <SettingsShell basePath="/admin/settings" />,
            children: [
              { index: true, element: <Navigate to="/admin/settings/profile" replace /> },
              { path: "profile", element: <ProfileSettingsPage /> },
              { path: "notifications", element: <NotificationsSettingsPage /> },
              { path: "usage", element: <UsageSettingsPage /> },
              { path: "storage", element: <StorageSettingsPage /> },
              { path: "plan", element: <PlanSettingsPage /> },
              { path: "billing", element: <BillingSettingsPage /> },
              { path: "branding", element: <BrandingSettingsPage /> },
              { path: "labs", element: <LabsSettingsPage /> },
              { path: "users", element: <UsersSettingsPage /> },
              { path: "projects", element: <ProjectsSettingsPage /> },
              { path: "fields", element: <FieldsSettingsPage /> },
              { path: "content-security", element: <ContentSecuritySettingsPage /> },
              { path: "actions", element: <ActionsSettingsPage /> },
              { path: "webhooks", element: <WebhooksSettingsPage /> },
              { path: "legal", element: <LegalNoticesSettingsPage /> },
            ],
          },
        ],
      },
    ],
  },

  // ── Watch pages (workspace-scoped, layout-less so the video canvas can own
  //    the viewport instead of being nested inside AppLayout/AdminLayout). ───
  {
    path: "/app/watch/:videoId",
    element: <RequireAuth />,
    errorElement: <RouteErrorBoundary />,
    children: [{ index: true, element: <WatchPage /> }],
  },
  {
    path: "/admin/watch/:videoId",
    element: <RequireRole roles={["ADMIN"]} />,
    errorElement: <RouteErrorBoundary />,
    children: [{ index: true, element: <WatchPage /> }],
  },

  // ── Compare pages (same layout-less pattern as watch — dual-player view). ─
  {
    path: "/app/compare/:aId/:bId",
    element: <RequireAuth />,
    errorElement: <RouteErrorBoundary />,
    children: [{ index: true, element: <ComparePage /> }],
  },
  {
    path: "/admin/compare/:aId/:bId",
    element: <RequireRole roles={["ADMIN"]} />,
    errorElement: <RouteErrorBoundary />,
    children: [{ index: true, element: <ComparePage /> }],
  },

  // ── Superadmin area ───────────────────────────────────────────────────────
  {
    path: "/superadmin",
    element: <RequireSuperadmin />,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        element: <SuperadminLayout />,
        children: [
          {
            index: true,
            element: <SuperadminDashboard />,
          },
          {
            path: "workspaces",
            element: (
              <PlaceholderPage
                title="All workspaces"
                description="List, extend (+40d), suspend, view usage."
              />
            ),
          },
        ],
      },
    ],
  },

  // catch-all — 404 for anything unmatched.
  { path: "*", element: <NotFoundPage /> },
]);
